import React from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Pickaxe, Trees } from 'lucide-react';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import RiskScoreCard from 'src/shared/components/risk-score-card/RiskScoreCard';
import NatureStatusCard from 'src/shared/components/nature-status-card/NatureStatusCard';
import ReportingFrameworks from 'src/shared/components/reporting-frameworks/ReportingFrameworks';
import {
    useGetSiteNatureRiskOverviewQuery,
    useSetSiteNatureStatusMutation,
} from 'src/app/site-nature-risk/state/api/siteNatureRiskEngineSlice';
import MethodologySheet from 'src/app/portfolio/views/global-nature-risk/overview/components/MethodologySheet';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { SiteNatureRiskViews } from 'src/routes/routesConfig';
import { usePortfolioSitesForQuadrant } from './components/usePortfolioSitesForQuadrant';
import RiskDynamicsBreakdownCard from './components/RiskDynamicsBreakdownCard';

const FEATURE_ID = 'risk-insights';

const SiteNatureRiskOverview = () => {
    const [isMethodologyOpen, setIsMethodologyOpen] = React.useState(false);
    const { siteId } = useParams<{ siteId: string }>();
    const { currentCustomerId } = useCustomer();
    const navigation = useAppNavigation();
    const { enabled, loading } = useFeatureFlag({ featureId: FEATURE_ID });
    const [setSiteNatureStatus] = useSetSiteNatureStatusMutation();

    const { data, isLoading } = useGetSiteNatureRiskOverviewQuery(
        { organisationBusinessCentralId: currentCustomerId, siteId: siteId! },
        { skip: !currentCustomerId || !siteId }
    );

    const {
        sites: portfolioSites,
        truncated: portfolioTruncated,
        siteLimit,
        isLoading: portfolioLoading,
        totalCount: portfolioTotalCount,
    } = usePortfolioSitesForQuadrant(currentCustomerId);

    if (loading) return <Loader />;
    if (!enabled) return <PageNotFound />;

    const site = data?.site;
    const riskSummary = site?.riskSummary ?? null;
    const portfolioRank = site?.portfolioRank ?? null;

    const breadcrumbs = [{ label: 'Nature Risk', path: navigation.globalNatureRiskOverviewPath }, { label: site?.name ?? '' }];

    const impactsAndDependenciesPath = navRoutesConfig[SiteNatureRiskViews.IMPACTS_AND_DEPENDENCIES]({
        siteId: siteId ?? '',
        customerId: currentCustomerId ?? '',
    });
    const stateOfNaturePath = navRoutesConfig[SiteNatureRiskViews.STATE_OF_NATURE]({
        siteId: siteId ?? '',
        customerId: currentCustomerId ?? '',
    });

    return (
        <>
            <StandardAppLayout
                title={site?.name ?? ''}
                subTitle='Site overview of nature-related risks'
                stickyHeader={false}
                breadcrumbs={breadcrumbs}
                mainContent={
                    <div className='flex flex-col w-full gap-6 flex-1 min-h-0 pt-1 mb-6' data-testid='site-nature-risk-overview-main'>
                        {/* Physical Nature Risk section */}
                        <div className='grid grid-cols-3 gap-4'>
                            <RiskScoreCard
                                title='Physical Nature Risk'
                                value={riskSummary?.physicalNatureRisk ?? null}
                                scoreType='Risk'
                                isLoading={isLoading}
                                size='lg'
                                slug='physical-nature-risk'
                                explainerSlug='physical-nature-risk-te'
                                portfolioRank={portfolioRank}
                                scoreLabel='Physical Risk Score'
                            />
                            <div className='col-span-2'>
                                <NatureStatusCard
                                    text={site?.natureStatus?.content ?? null}
                                    createdAt={site?.natureStatus?.createdAt ?? null}
                                    isLoading={isLoading}
                                    onSave={async content => {
                                        await setSiteNatureStatus({
                                            organisationBusinessCentralId: currentCustomerId,
                                            siteId: siteId!,
                                            content,
                                        }).unwrap();
                                    }}
                                />
                            </div>
                        </div>

                        {/* Risk Dynamics Breakdown */}
                        <RiskDynamicsBreakdownCard
                            sites={portfolioSites}
                            highlightedSiteId={site?.id}
                            truncated={portfolioTruncated}
                            siteLimit={siteLimit}
                            isLoading={portfolioLoading}
                            totalCount={portfolioTotalCount}
                        />

                        {/* Contributors section */}
                        <div className='flex flex-col gap-4'>
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-3'>
                                    <span className='text-[20px] font-normal text-[#212121]'>Physical Nature Risk contributors</span>
                                    <button
                                        type='button'
                                        data-testid='contributors-learn-more-btn'
                                        className='border border-border rounded-lg px-2 py-0.5 text-xs text-foreground'
                                        onClick={() => setIsMethodologyOpen(true)}
                                    >
                                        Learn more
                                    </button>
                                </div>
                                <ReportingFrameworks slugPrefix='gnr' />
                            </div>
                            <div className='grid grid-cols-3 gap-4'>
                                <RiskScoreCard
                                    title='Dependencies'
                                    value={riskSummary?.operationalDependencies ?? null}
                                    scoreType='Risk'
                                    isLoading={isLoading}
                                    size='md'
                                    icon={Building2}
                                    slug='dependencies'
                                    explainerSlug='dependencies-te'
                                    exploreTo={impactsAndDependenciesPath}
                                />
                                <RiskScoreCard
                                    title='Impact on Nature'
                                    value={riskSummary?.impactsOnNature ?? null}
                                    scoreType='Risk'
                                    isLoading={isLoading}
                                    size='md'
                                    icon={Pickaxe}
                                    slug='impact-on-nature'
                                    explainerSlug='impact-on-nature-te'
                                    exploreTo={impactsAndDependenciesPath}
                                />
                                <RiskScoreCard
                                    title='State of Nature'
                                    value={riskSummary?.stateOfNature ?? null}
                                    isLoading={isLoading}
                                    size='md'
                                    icon={Trees}
                                    slug='state-of-nature'
                                    explainerSlug='state-of-nature-te'
                                    scoreType='Condition'
                                    exploreTo={stateOfNaturePath}
                                />
                            </div>
                        </div>
                    </div>
                }
            />
            <MethodologySheet
                open={isMethodologyOpen}
                onOpenChange={setIsMethodologyOpen}
                onStateOfNature={siteId ? () => navigation.toSiteNatureRiskStateOfNature(siteId) : undefined}
            />
        </>
    );
};

export default SiteNatureRiskOverview;
